import React from 'react';
import { BUTTON_BASE_CHROME } from '../Button/Button';
import { IconClose } from '../../assets/icons/IconClose';
import { BannerIcon } from './BannerIcon';
import type { BannerAction, BannerDismiss, BannerProps, BannerTone } from './types';

const TONE_SURFACE: Record<BannerTone, string> = {
    info: 'bg-surface-info',
    success: 'bg-surface-success',
    warning: 'bg-surface-warning',
    danger: 'bg-surface-danger',
};

const TONE_TEXT: Record<BannerTone, string> = {
    info: 'text-info',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
};

const TONE_BORDER: Record<BannerTone, string> = {
    info: 'border-info-100',
    success: 'border-success-100',
    // The bright warning-100 yellow is too faint as a hairline, so warning outlines take the -200 step.
    warning: 'border-warning-200',
    danger: 'border-danger-100',
};

/** Outline colours the action button borrows from its own banner's tone rather than the
 * generic grey secondary Button — the notice's action reads as "part of this notice", and the
 * hover fill reuses the same tinted surface the banner itself sits on. The border matches the
 * banner's own outline shade (TONE_BORDER), so the button doesn't stand out as more heavily
 * outlined than the notice it belongs to. */
const TONE_ACTION: Record<BannerTone, string> = {
    info: 'border-info-100 text-info hover:bg-surface-info',
    success: 'border-success-100 text-success hover:bg-surface-success',
    warning: 'border-warning-200 text-warning hover:bg-surface-warning',
    danger: 'border-danger-100 text-danger hover:bg-surface-danger',
};

const BannerActionButton: React.FC<{ tone: BannerTone; action: BannerAction; className?: string }> = ({ tone, action, className = '' }) => (
    <button
        type="button"
        data-testid={action.testId}
        onClick={action.onClick}
        disabled={action.disabled}
        className={`h-9 ${BUTTON_BASE_CHROME} border bg-transparent px-l text-s font-semibold ${className} ` +
            (action.disabled
                ? 'cursor-not-allowed border-line-subtle text-content-tertiary'
                : `cursor-pointer ${TONE_ACTION[tone]}`)}
    >
        {action.label}
    </button>
);

const BannerDismissButton: React.FC<{ tone: BannerTone; dismiss: BannerDismiss }> = ({ tone, dismiss }) => (
    <button
        type="button"
        data-testid={dismiss.testId}
        onClick={dismiss.onClick}
        aria-label={dismiss.label}
        className={`-mt-xs -mr-s shrink-0 cursor-pointer rounded-full p-1.5 transition-colors hover:bg-overlay-weak focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring ${TONE_TEXT[tone]}`}
    >
        <IconClose className="h-4 w-4" />
    </button>
);

/** Warnings and errors interrupt; info and success are announced without stealing focus. */
const TONE_ROLE: Record<BannerTone, 'alert' | 'status'> = {
    info: 'status',
    success: 'status',
    warning: 'alert',
    danger: 'alert',
};

/**
 * The app's one contextual notice. Every tone shares the same shell — tinted surface, status
 * glyph, optional emphasized title, optional body, optional trailing action — so a new notice
 * is a tone and some copy rather than another hand-rolled coloured div.
 *
 * No entrance animation, deliberately: the techniques that would smooth a late-arriving banner (a
 * grid-template-rows expansion, @starting-style, the translate-* utilities) all need a newer Chromium than
 * this app targets, and the fallback that does work there was too weak to justify the mount-timing code.
 */
export const Banner: React.FC<BannerProps> = ({ tone, title, children, action, dismiss, testId, className = '', centered = false }) => {
    if (centered) {
        return (
            <div
                data-testid={testId}
                role={TONE_ROLE[tone]}
                className={`flex flex-col items-center gap-m rounded-lg border ${TONE_BORDER[tone]} px-l py-xl text-center ${TONE_SURFACE[tone]} ${className}`}
            >
                <BannerIcon tone={tone} size="lg" />

                <div className="flex flex-col items-center gap-s">
                    {title && (
                        <p data-testid={testId && `${testId}-title`} className={`text-s font-bold ${TONE_TEXT[tone]}`}>
                            {title}
                        </p>
                    )}
                    {children && <div className={`text-s font-normal ${TONE_TEXT[tone]}`}>{children}</div>}
                </div>

                {action && <BannerActionButton tone={tone} action={action} />}
            </div>
        );
    }

    return (
        <div
            data-testid={testId}
            role={TONE_ROLE[tone]}
            className={`flex flex-col gap-s rounded-lg border ${TONE_BORDER[tone]} px-l pt-m pb-l ${TONE_SURFACE[tone]} ${className}`}
        >
            <div className={`flex ${children ? 'items-start' : 'items-center'} gap-m`}>
                <BannerIcon tone={tone} />

                <div className="flex min-w-0 flex-1 flex-col gap-xs">
                    {title && (
                        <p data-testid={testId && `${testId}-title`} className={`text-s font-bold ${TONE_TEXT[tone]}`}>
                            {title}
                        </p>
                    )}
                    {children && <div className={`text-s font-normal ${TONE_TEXT[tone]}`}>{children}</div>}
                </div>

                {dismiss && <BannerDismissButton tone={tone} dismiss={dismiss} />}
            </div>

            {action && (
                <div className="flex justify-end">
                    <BannerActionButton tone={tone} action={action} />
                </div>
            )}
        </div>
    );
};
